import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useBeachvisionStore } from '../../store/beachvisionStore';
import { BeachvisionLayout } from '../../components/beachvision/BeachvisionLayout';
import { LazyFrameImage } from '../../components/beachvision/LazyFrameImage';
import { DebugDrawer } from '../../components/beachvision/DebugDrawer';
import { DebugPanel } from '../../components/beachvision/DebugPanel';
import { PageErrorBoundary } from '../../components/beachvision/PageErrorBoundary';
import { useAnalysis } from '../../hooks/useAnalysis';
import { getAnalysis, getAnalysisFrames, getAnalysisPoint, getPointFrame } from '../../api/beachvision';
import { PointValidationFeedback } from '../../components/beachvision/PointValidationFeedback';
import { isDebugModeEnabled } from '../../lib/debug-mode';
import { buildQualityCheckDebugItems } from '../../lib/quality-check-debug';

function formatClipMoment(bestFrameMs, pointStartMs) {
  if (bestFrameMs == null || pointStartMs == null) return null;
  const absoluteMs = Number(bestFrameMs);
  const clipStartMs = Number(pointStartMs);
  if (!Number.isFinite(absoluteMs) || !Number.isFinite(clipStartMs)) return null;
  const elapsedSeconds = Math.max(0, Math.floor((absoluteMs - clipStartMs) / 1000));
  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = String(elapsedSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}

/** New presigns of the same clip only differ in query; keep prior URL so <video src> does not reset playback. */
function isSameClipVideoSource(prevUrl, nextUrl) {
  if (!prevUrl || !nextUrl) return false;
  if (prevUrl === nextUrl) return true;
  try {
    const a = new URL(prevUrl, typeof window !== 'undefined' ? window.location.href : 'http://localhost/');
    const b = new URL(nextUrl, typeof window !== 'undefined' ? window.location.href : 'http://localhost/');
    return a.origin === b.origin && a.pathname === b.pathname;
  } catch {
    return false;
  }
}

function PointDetailInner({ currentUser }) {
  const { id, pid } = useParams();
  const storedAnalysis = useBeachvisionStore((s) => s.analysesBySessionId[String(id)]);
  const setSessionAnalysis = useBeachvisionStore((s) => s.setSessionAnalysis);
  const {
    data: analysisRes,
    isLoading: analysisLoading,
    refetch: refetchAnalysis,
  } = useAnalysis(id, { enabled: !storedAnalysis, refetchWhileProcessingMs: 4000 });
  const analysis = storedAnalysis || (analysisRes?.ok ? analysisRes.data : null);
  const [pointDetail, setPointDetail] = useState(null);
  /** While re-scoring, the API returns a new signed URL every poll; do not pass each one to <video> or playback restarts. */
  const [stableClipVideoUrl, setStableClipVideoUrl] = useState(null);
  const [clipError, setClipError] = useState(false);
  const [linkFramesByClipId, setLinkFramesByClipId] = useState({});
  const [fallbackFramesByTakeawayId, setFallbackFramesByTakeawayId] = useState({});
  const debugModeEnabled = isDebugModeEnabled(currentUser);

  const point = useMemo(() => {
    if (!analysis?.points) return null;
    return analysis.points.find((p) => String(p.id) === String(pid));
  }, [analysis, pid]);

  const sortedPoints = useMemo(() => {
    if (!analysis?.points?.length) return [];
    return [...analysis.points].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [analysis?.points]);

  useEffect(() => {
    if (!storedAnalysis && analysisRes?.ok && analysisRes.data) {
      setSessionAnalysis(id, analysisRes.data);
    }
  }, [analysisRes, id, setSessionAnalysis, storedAnalysis]);

  useEffect(() => {
    if (!analysis?.id || !pid) return;
    let cancelled = false;
    setPointDetail(null);
    setStableClipVideoUrl(null);
    getAnalysisPoint(analysis.id, pid)
      .then((data) => {
        if (!cancelled) setPointDetail(data);
      })
      .catch(() => {
        if (!cancelled) setPointDetail(null);
      });
    return () => { cancelled = true; };
  }, [analysis?.id, pid]);

  const handlePointFeedbackRefresh = useCallback(async () => {
    if (analysis?.id && pid) {
      try {
        setPointDetail(await getAnalysisPoint(analysis.id, pid));
      } catch {
        /* ignore */
      }
    }
    try {
      const r = await getAnalysis(id);
      if (r.ok) setSessionAnalysis(id, r.data);
    } catch {
      /* ignore */
    }
  }, [id, analysis?.id, pid, setSessionAnalysis]);

  useEffect(() => {
    const next = pointDetail?.clip_video_url || null;
    const busy = Boolean(pointDetail?.reanalysis_in_progress);
    if (!next) {
      if (!busy) setStableClipVideoUrl(null);
      return;
    }
    if (!busy) {
      setStableClipVideoUrl((prev) => {
        if (prev && isSameClipVideoSource(prev, next)) return prev;
        return next;
      });
    } else {
      setStableClipVideoUrl((prev) => prev || next);
    }
  }, [
    pointDetail?.clip_video_url,
    pointDetail?.reanalysis_in_progress,
    pointDetail?.id,
  ]);

  useEffect(() => {
    setFallbackFramesByTakeawayId({});
  }, [pointDetail?.id, point?.id]);

  useEffect(() => {
    if (!analysis?.id) return;
    let cancelled = false;
    getAnalysisFrames(analysis.id)
      .then((payload) => {
        if (cancelled) return;
        const linkMap = {};
        (payload?.takeaway_point_frames || []).forEach((row) => {
          if (row?.clip_id && row?.frame_url) {
            linkMap[String(row.clip_id)] = row.frame_url;
          }
        });
        setLinkFramesByClipId(linkMap);
      })
      .catch(() => {
        if (!cancelled) setLinkFramesByClipId({});
      });
    return () => { cancelled = true; };
  }, [analysis?.id, pointDetail?.analysis_revision, pointDetail?.id]);

  const pointView = pointDetail || point;

  const adjacentPoints = useMemo(() => {
    if (!pointView?.id || !sortedPoints.length) return { prev: null, next: null };
    const idx = sortedPoints.findIndex((p) => String(p.id) === String(pointView.id));
    if (idx < 0) return { prev: null, next: null };
    return {
      prev: idx > 0 ? sortedPoints[idx - 1] : null,
      next: idx < sortedPoints.length - 1 ? sortedPoints[idx + 1] : null,
    };
  }, [sortedPoints, pointView]);

  const clipUrl = stableClipVideoUrl || null;
  const clipDurationMs = useMemo(() => {
    if (!pointView) return null;
    const start = Number(pointView.timestamp_start_ms);
    const end = Number(pointView.timestamp_end_ms);
    if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
    return Math.max(0, end - start);
  }, [pointView]);
  const pointTakeaways = useMemo(() => {
    if (!pointView) return [];
    const takeawaysById = new Map(
      (analysis?.takeaways || []).map((takeaway) => [String(takeaway.id), takeaway])
    );
    const framesByTakeawayId = new Map();
    (pointView.takeaway_frames || []).forEach((frame) => {
      const takeawayId = String(frame?.takeaway_id || '');
      if (!takeawayId || framesByTakeawayId.has(takeawayId)) return;
      framesByTakeawayId.set(takeawayId, frame);
    });

    return (pointView.takeaway_tags || []).map((tag) => {
      const takeawayId = String(tag?.takeaway_id || '');
      const takeaway = takeawaysById.get(takeawayId);
      const frameMeta = framesByTakeawayId.get(takeawayId);
      const bestFrameMs = frameMeta?.best_frame_ms ?? takeaway?.best_shot_timestamp_ms ?? null;
      const clipMoment = formatClipMoment(bestFrameMs, pointView.timestamp_start_ms);
      const fromLink =
        frameMeta?.clip_id != null
          ? linkFramesByClipId[String(frameMeta.clip_id)]
          : null;
      // Bundle map can be stale after a point re-run (new clip_id) until we refetch /frames/; use fallback.
      const frameUrl = (fromLink || fallbackFramesByTakeawayId[takeawayId]) || null;
      const frameRequestKey = [
        String(analysis?.id ?? ''),
        String(pointView.id),
        takeawayId,
        String(bestFrameMs ?? ''),
        String(pointDetail?.analysis_revision ?? ''),
        String(frameMeta?.clip_id ?? ''),
      ].join('|');
      return {
        takeawayId,
        title: tag?.title || takeaway?.title || 'Untitled takeaway',
        score: tag?.score,
        category: tag?.category || takeaway?.category || null,
        touchOrder: tag?.touch_order ?? null,
        description: takeaway?.description || 'No detailed takeaway description yet.',
        frameUrl,
        bestFrameMs,
        clipMoment,
        frameRequestKey,
      };
    });
  }, [
    analysis?.id,
    analysis?.takeaways,
    fallbackFramesByTakeawayId,
    linkFramesByClipId,
    pointDetail?.analysis_revision,
    pointView,
  ]);

  const qualityCheckDebugItems = useMemo(
    () => buildQualityCheckDebugItems(analysis?.raw_gemini_response),
    [analysis?.raw_gemini_response]
  );

  useEffect(() => {
    if (!pointView) return;
    setClipError(false);
  }, [pointView]);

  if (!analysis && analysisLoading) {
    return (
      <div className="px-4 py-16 text-center flex flex-col items-center gap-3">
        <Loader2 className="h-8 w-8 text-beachvision-teal animate-spin" />
        <p className="text-beachvision-muted">Loading analysis...</p>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="px-4 py-16 text-center">
        <p className="text-beachvision-muted mb-4">
          Analysis is not ready yet.
        </p>
        <button
          type="button"
          onClick={() => refetchAnalysis()}
          className="w-full max-w-xs mx-auto bg-beachvision-cta text-beachvision-on-coral rounded-xl px-6 py-3 font-display font-bold mb-3"
        >
          Retry
        </button>
        <Link
          to={`/analysis/${id}/points/`}
          className="text-beachvision-coral font-display font-bold"
        >
          Go to analysis
        </Link>
      </div>
    );
  }

  if (!pointView) {
    return (
      <div className="px-4 py-16 text-center">
        <p className="text-beachvision-muted mb-4">
          This point could not be found.
        </p>
        <Link
          to={`/analysis/${id}/points/`}
          className="text-beachvision-coral font-display font-bold"
        >
          Back to points
        </Link>
      </div>
    );
  }

  const winnerLabel =
    pointView.winner === 'our_team'
      ? 'Won'
      : pointView.winner === 'opponent'
        ? 'Lost'
        : 'Unknown';
  const scoreLabel = pointView.score_label || (
    pointView.score_for != null && pointView.score_against != null
      ? `${pointView.score_for}-${pointView.score_against}`
      : null
  );

  const touchByTouchLog = Array.isArray(pointView?.touch_by_touch_log)
    ? pointView.touch_by_touch_log
    : [];

  const debugItems = [
    { label: 'Session ID', value: String(id || '-') },
    { label: 'Point ID', value: String(pointView?.id || '-') },
    { label: 'Winner', value: String(pointView?.winner || '-') },
    {
      label: 'Time window',
      value: `${pointView?.timestamp_start_ms ?? '-'}ms -> ${pointView?.timestamp_end_ms ?? '-'}ms`,
    },
    {
      label: 'Clip duration',
      value:
        clipDurationMs == null
          ? '-'
          : `${clipDurationMs}ms (${(clipDurationMs / 1000).toFixed(2)}s)`,
    },
    { label: 'Best shot', value: `${pointView?.best_shot_timestamp_ms ?? '-'}ms` },
    { label: 'Clip source', value: clipUrl ? 's3-serialized' : 'none' },
    {
      label: 'Play-by-play (touch_by_touch_log)',
      value: touchByTouchLog,
    },
  ];

  return (
    <>
      <header className="px-4 pt-6 flex items-center gap-3">
        <Link
          to={`/analysis/${id}/points/`}
          className="p-2.5 rounded-xl bg-beachvision-surface-low text-white"
          aria-label="Back to analysis"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1 min-w-0">
          <h1 className="font-display font-black text-white uppercase text-sm tracking-wide truncate leading-tight">
            Point {pointView.order}
          </h1>
          <p className="text-xs text-beachvision-muted mt-1 uppercase tracking-wide">
            {winnerLabel} {scoreLabel ? `· ${scoreLabel}` : ''}
          </p>
        </div>
        <div
          className="flex items-center gap-0.5 shrink-0 rounded-xl ring-2 ring-beachvision-coral/50 bg-beachvision-elevated p-0.5"
          role="group"
          aria-label="Adjacent points"
        >
          {adjacentPoints.prev ? (
            <Link
              to={`/analysis/${id}/points/${adjacentPoints.prev.id}/`}
              className="p-2 rounded-lg text-beachvision-teal hover:bg-white/5 transition-colors"
              aria-label={`Previous point, point ${adjacentPoints.prev.order}`}
            >
              <ChevronLeft className="h-5 w-5" strokeWidth={2.25} aria-hidden />
            </Link>
          ) : (
            <span
              className="p-2 rounded-lg text-beachvision-muted/35 pointer-events-none"
              aria-hidden
            >
              <ChevronLeft className="h-5 w-5" strokeWidth={2.25} />
            </span>
          )}
          {adjacentPoints.next ? (
            <Link
              to={`/analysis/${id}/points/${adjacentPoints.next.id}/`}
              className="p-2 rounded-lg text-beachvision-teal hover:bg-white/5 transition-colors"
              aria-label={`Next point, point ${adjacentPoints.next.order}`}
            >
              <ChevronRight className="h-5 w-5" strokeWidth={2.25} aria-hidden />
            </Link>
          ) : (
            <span
              className="p-2 rounded-lg text-beachvision-muted/35 pointer-events-none"
              aria-hidden
            >
              <ChevronRight className="h-5 w-5" strokeWidth={2.25} />
            </span>
          )}
        </div>
      </header>

      <div className="px-4 mt-4">
        <div className="rounded-2xl overflow-hidden bg-beachvision-surface-low min-h-48">
          {clipError || !clipUrl ? (
            <div className="w-full h-48 flex items-center justify-center">
              <p className="text-xs text-beachvision-muted font-display font-bold uppercase tracking-wide">
                Clip unavailable
              </p>
            </div>
          ) : null}
          {clipUrl ? (
            <video
              src={clipUrl}
              className="w-full h-48 object-contain bg-black"
              controls
              autoPlay
              playsInline
              loop
              onError={() => setClipError(true)}
            />
          ) : null}
        </div>

        <h2 className="font-display font-bold text-xl text-white mt-6">
          {pointView.point_title || pointView.summary_tag || `Point ${pointView.order}`}
        </h2>
        <p className="text-beachvision-muted mt-3 leading-relaxed text-sm">
          {pointView.point_summary || 'Coaching summary not available yet.'}
        </p>

        {analysis?.id &&
        (pointView?.processing_status === 'complete' || pointView?.reanalysis_in_progress) ? (
          <PointValidationFeedback
            point={pointView}
            analysisId={analysis.id}
            onRefresh={handlePointFeedbackRefresh}
          />
        ) : null}

        <section className="mt-6">
          <h3 className="font-display font-bold text-white text-sm uppercase tracking-wide">
            Point takeaways
          </h3>
          {pointTakeaways.length === 0 ? (
            <p className="text-beachvision-muted text-sm mt-3">
              No takeaways were linked to this point yet.
            </p>
          ) : (
            <div className="mt-3 space-y-3">
              {pointTakeaways.map((takeaway) => (
                <Link
                  key={`${pointView.id}-${takeaway.takeawayId}`}
                  to={`/analysis/${id}/${
                    takeaway.category === 'strength' ? 'strengths' : 'takeaways'
                  }/${takeaway.takeawayId}/`}
                  className="block rounded-xl border border-white/5 bg-beachvision-surface-low p-3"
                >
                  <div>
                    <div className="flex items-center justify-between gap-3">
                      <span
                        className={`text-[11px] px-2.5 py-1 rounded-full font-display font-bold uppercase tracking-wide ${
                          takeaway.category === 'strength' || takeaway.score >= 60
                            ? 'bg-beachvision-teal/20 text-beachvision-teal'
                            : 'bg-beachvision-coral/20 text-beachvision-coral'
                        }`}
                      >
                        {takeaway.title}
                      </span>
                      <p className="text-[11px] text-beachvision-muted uppercase tracking-wide shrink-0">
                        {takeaway.clipMoment ? `${takeaway.clipMoment}s` : '--'}
                      </p>
                    </div>
                    {takeaway.touchOrder != null ? (
                      <p className="text-[11px] text-beachvision-muted uppercase tracking-wide mt-2">
                        Touch #{takeaway.touchOrder}
                      </p>
                    ) : null}

                    <div className="mt-3 grid grid-cols-2 gap-3 items-stretch">
                      <p className="text-sm text-beachvision-muted leading-relaxed">
                        {takeaway.description}
                      </p>
                      <LazyFrameImage
                        className="w-full h-40 rounded-lg overflow-hidden"
                        frameRequestKey={takeaway.frameRequestKey}
                        loadImage={() => {
                          if (takeaway.frameUrl) return Promise.resolve(takeaway.frameUrl);
                          if (!analysis?.id || !pointView?.id || takeaway.bestFrameMs == null) {
                            return Promise.resolve(null);
                          }
                          return getPointFrame(analysis.id, pointView.id, takeaway.bestFrameMs)
                            .then(({ frame_url }) => {
                              if (!frame_url) return null;
                              setFallbackFramesByTakeawayId((prev) => ({
                                ...prev,
                                [takeaway.takeawayId]: frame_url,
                              }));
                              return frame_url;
                            })
                            .catch(() => null);
                        }}
                        frameUnavailable={!takeaway.frameUrl && takeaway.bestFrameMs == null}
                        alt={`${takeaway.title} frame`}
                      />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {adjacentPoints.prev || adjacentPoints.next ? (
          <nav
            className="mt-5 flex border-t border-white/5 pt-4"
            aria-label="Point navigation"
          >
            <div className="flex-1 flex justify-start min-h-[1.25rem]">
              {adjacentPoints.prev ? (
                <Link
                  to={`/analysis/${id}/points/${adjacentPoints.prev.id}/`}
                  className="inline-flex items-center gap-0.5 text-[11px] text-beachvision-muted/90 hover:text-beachvision-muted font-display font-semibold uppercase tracking-wide transition-colors"
                >
                  <ChevronLeft className="h-3.5 w-3.5 opacity-70" strokeWidth={2.25} aria-hidden />
                  Point {adjacentPoints.prev.order}
                </Link>
              ) : null}
            </div>
            <div className="flex-1 flex justify-end min-h-[1.25rem]">
              {adjacentPoints.next ? (
                <Link
                  to={`/analysis/${id}/points/${adjacentPoints.next.id}/`}
                  className="inline-flex items-center gap-0.5 text-[11px] text-beachvision-muted/90 hover:text-beachvision-muted font-display font-semibold uppercase tracking-wide transition-colors"
                >
                  Point {adjacentPoints.next.order}
                  <ChevronRight className="h-3.5 w-3.5 opacity-70" strokeWidth={2.25} aria-hidden />
                </Link>
              ) : null}
            </div>
          </nav>
        ) : null}

        <DebugDrawer enabled={debugModeEnabled}>
          <DebugPanel title="Admin Debug" items={debugItems} />
          <DebugPanel title="Quality check (intake)" items={qualityCheckDebugItems} />
        </DebugDrawer>
      </div>
    </>
  );
}

export default function PointDetailPage(props) {
  return (
    <BeachvisionLayout>
      <PageErrorBoundary>
        <PointDetailInner {...props} />
      </PageErrorBoundary>
    </BeachvisionLayout>
  );
}
